import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { Colors } from "../../utilities/colors";

const sharedStyles = `
  background-color: ${Colors.inputBg};
  border-radius: 24px;
  border: 0;
  padding: 12px 20px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${Colors.dark};
`;

const InputWrapper = styled.div`
  width: 100%;
  min-width: 0;
`;

const InputField = styled.input`
  ${sharedStyles}

  padding-left: ${(props) => props.left || "20px"};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "text")};

  &:hover,
  &:focus,
  &:active {
    box-shadow: ${(props) =>
      props.disabled ? "none" : "0 0 0 1px #ff60b1"};
    border-color: ${(props) =>
      props.disabled ? "transparent" : "#ff60b1"};
  }

  &:disabled {
    opacity: 1;
  }
`;

const AutoResizeTextarea = styled.textarea`
  ${sharedStyles}

  min-height: 46px;
  max-height: ${(props) => props.$maxHeight || "300px"};
  padding-left: ${(props) => props.left || "20px"};

  resize: none;
  overflow-x: hidden;
  overflow-y: hidden;

  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  font-family: inherit;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "text")};

  &:hover,
  &:focus,
  &:active {
    box-shadow: ${(props) =>
      props.disabled ? "none" : "0 0 0 1px #ff60b1"};
    border-color: ${(props) =>
      props.disabled ? "transparent" : "#ff60b1"};
  }

  &:disabled,
  &:read-only {
    opacity: 1;
  }
`;

const MaxLimitMessage = styled.div`
  width: 100%;
  margin-top: 6px;

  color: #d04c8d;
  font-family: "General Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

export default function Input({
  multiline = false,
  value = "",
  onChange,
  maxLength,
  maxHeight = "300px",
  disabled = false,
  showMaxLimitMessage = true,
  maxLimitMessage = "Max limit reached",
  ...props
}) {
  const textareaRef = useRef(null);

  const currentValue = String(value || "");
  const currentLength = currentValue.length;

  const hasReachedMaxLimit =
    !disabled &&
    Boolean(maxLength) &&
    currentLength >= Number(maxLength);

  const resizeTextarea = () => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.style.height = "auto";

    const maximumHeight = parseInt(maxHeight, 10) || 300;
    const newHeight = Math.min(
      textarea.scrollHeight,
      maximumHeight
    );

    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > maximumHeight
        ? "auto"
        : "hidden";
  };

  useEffect(() => {
    if (multiline) {
      resizeTextarea();
    }
  }, [value, multiline, maxHeight]);

  const handleTextareaChange = (event) => {
    let newValue = event.target.value;

    if (
      maxLength &&
      newValue.length > Number(maxLength)
    ) {
      newValue = newValue.slice(
        0,
        Number(maxLength)
      );
    }

    const updatedEvent = {
      ...event,
      target: {
        ...event.target,
        name: event.target.name,
        value: newValue,
      },
    };

    if (onChange) {
      onChange(updatedEvent);
    }

    requestAnimationFrame(resizeTextarea);
  };

  return (
    <InputWrapper>
      {multiline ? (
        <AutoResizeTextarea
          {...props}
          ref={textareaRef}
          value={currentValue}
          onChange={handleTextareaChange}
          maxLength={maxLength}
          $maxHeight={maxHeight}
          disabled={disabled}
          rows={1}
        />
      ) : (
        <InputField
          {...props}
          value={currentValue}
          onChange={onChange}
          maxLength={maxLength}
          disabled={disabled}
        />
      )}

      {showMaxLimitMessage && hasReachedMaxLimit && (
        <MaxLimitMessage>
          {maxLimitMessage}
        </MaxLimitMessage>
      )}
    </InputWrapper>
  );
}